<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: User Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.user_name')"
                v-model.trim="filterOptions.user_name"
              />
              <!-- End:: User Name Input -->

              <!-- Start:: User Mobile Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.phone')"
                v-model.trim="filterOptions.user_mobile"
              />
              <!-- End:: User Mobile Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="4"
                :optionsList="statusOptions"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.reviews_management") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <template v-slot:[`item.id`]="{ item, index }">
          <p class="blue-grey--text text--darken-1 fs-3" v-if="!item.id">-</p>
          <p v-else>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>

        <template v-slot:[`item.user`]="{ item }">
          <div v-if="item.user" class="review-cell">
            <p class="mb-0 review-main-text">{{ item.user.name }}</p>
            <small class="review-sub-text">{{ item.user.mobile }}</small>
          </div>
          <span v-else class="text-danger">-</span>
        </template>

        <template v-slot:[`item.order`]="{ item }">
          <div v-if="item.order" class="review-cell">
            <p class="mb-0 review-main-text">#{{ item.order.serial_number }}</p>
            <small class="review-sub-text">{{ item.order.service_name }}</small>
          </div>
          <span v-else class="text-danger">-</span>
        </template>

        <template v-slot:[`item.rate`]="{ item }">
          <div class="rate d-flex align-items-center justify-content-center">
            <span class="me-1">{{ item.rate }}</span>
            <i class="fas fa-star text-warning"></i>
          </div>
        </template>

        <template v-slot:[`item.comment`]="{ item }">
          <h6 class="text-danger" v-if="!item.comment">---</h6>
          <div
            class="comment-preview"
            v-else
            @click="showDescriptionModal(item.comment)"
          >
            <i class="fas fa-file-alt"></i>
          </div>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            dark
            small
          >
            {{ item.status_label || item.status }}
          </v-chip>
        </template>

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Approve Button -->
            <a-tooltip
              placement="bottom"
              v-if="item.status !== 'approved'"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.approve") }}</span>
              </template>
              <span>
                <button 
                  class="btn_activate" 
                  @click="approveReview(item)"
                  :disabled="approvingReviewId === item.id || deletingReviewId === item.id"
                  type="button"
                >
                  <i v-if="approvingReviewId === item.id" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-check"></i>
                </button>
              </span>
            </a-tooltip>

            <!-- Delete Button -->
            <a-tooltip
              placement="bottom"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <span>
                <button 
                  class="btn_delete" 
                  @click="selectDeleteItem(item)"
                  :disabled="approvingReviewId === item.id || deletingReviewId === item.id"
                  type="button"
                >
                  <i v-if="deletingReviewId === item.id" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fal fa-trash-alt"></i>
                </button>
              </span>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Description Modal -->
          <description-modal
            v-if="dialogDescription"
            :modalIsOpen="dialogDescription"
            :modalDesc="selectedDescriptionTextToShow"
            @toggleModal="dialogDescription = !dialogDescription"
          />
          <!-- End:: Description Modal -->

          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete" max-width="500">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{ $t("TITLES.deleteReviewConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn 
                  class="modal_confirm_btn" 
                  @click="confirmDeleteItem"
                  :disabled="deletingReviewId !== null"
                  :loading="deletingReviewId !== null"
                >
                  {{ $t("BUTTONS.ok") }}
                </v-btn>
                <v-btn 
                  class="modal_cancel_btn" 
                  @click="dialogDelete = false"
                  :disabled="deletingReviewId !== null"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'"
          :next-icon="getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'"
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "AllReviews",
    computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    statusOptions() {
      return [
        { id: "pending", name: this.$t("STATUS.pending"), value: "pending" },
        { id: "approved", name: this.$t("STATUS.approved"), value: "approved" },
        { id: "rejected", name: this.$t("STATUS.rejected"), value: "rejected" },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      isWaitingRequest: false,
      loading: false,
      approvingReviewId: null,
      deletingReviewId: null,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        user_name: null,
        user_mobile: null,
        status: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.user"),
          value: "user",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.order"),
          value: "order",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.rate"),
          value: "rate",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.comment"),
          value: "comment",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "150px",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Data
      dialogDescription: false,
      selectedDescriptionTextToShow: "",
      dialogDelete: false,
      itemToDelete: null,
      // End:: Dialogs Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    getStatusColor(status) {
      const colors = {
        pending: "orange",
        approved: "success",
        rejected: "error",
      };
      return colors[status] || "grey";
    },

    showDescriptionModal(description) {
      this.dialogDescription = true;
      this.selectedDescriptionTextToShow = description;
    },

    // Start:: Handle Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/reviews/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.user_name = null;
      this.filterOptions.user_mobile = null;
      this.filterOptions.status = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/reviews/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    // End:: Handle Filter

    // Start:: Pagination
    updateRouterQueryParam(paginationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: paginationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
    // End:: Pagination

    // Start:: Set Table Rows
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "reviews",
          params: {
            page: this.paginations.current_page,
            user_name: this.filterOptions.user_name,
            user_mobile: this.filterOptions.user_mobile,
            status: this.filterOptions.status?.value,
          },
        });
        this.tableRows = res.data.data.data || [];
        this.paginations.last_page = res.data.data.meta.last_page || 1;
        this.paginations.items_per_page = res.data.data.meta.per_page || 10;
      } catch (error) {
        console.log(error.response?.data?.message);
        this.$message.error(error.response?.data?.message || 'حدث خطأ في جلب البيانات');
      } finally {
        this.loading = false;
      }
    },
    // End:: Set Table Rows

    // Start:: Approve Review
    async approveReview(item) {
      this.approvingReviewId = item.id;
      try {
        await this.$axios({
          method: "POST",
          url: `reviews/${item.id}/approve`,
        });
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.reviewApproved"));
      } catch (error) {
        this.$message.error(error.response?.data?.message || 'حدث خطأ في الموافقة على التقييم');
      } finally {
        this.approvingReviewId = null;
      }
    },
    // End:: Approve Review

    // Start:: Delete
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },

    async confirmDeleteItem() {
      if (!this.itemToDelete) return;
      
      this.deletingReviewId = this.itemToDelete.id;
      try {
        await this.$axios({
          method: "DELETE",
          url: `reviews/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.$message.error(error.response?.data?.message || 'حدث خطأ في حذف التقييم');
      } finally {
        this.deletingReviewId = null;
        if (this.dialogDelete) {
          this.dialogDelete = false;
        }
      }
    },
    // End:: Delete
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
  },
};
</script>

<style scoped>
.comment-preview {
  cursor: pointer;
}
.comment-preview i {
  font-size: 20px;
  color: #3eb6d4;
}
.comment-preview:hover i {
  color: #2a8ba8;
}

.actions {
  display: flex !important;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 100px;
  width: 100%;
  padding: 5px;
}

.actions span {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
}

.actions button {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  min-width: 35px;
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  padding: 0;
  margin: 0 2px;
}

.actions button.btn_activate i {
  font-size: 20px;
  color: #4caf50;
}

.actions button.btn_delete i {
  font-size: 18px;
  color: #f44336;
}

.actions button:hover {
  opacity: 0.8;
  transform: scale(1.1);
}

.actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.actions .fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.review-main-text {
  color: var(--theme_text_clr);
}

.review-sub-text {
  color: var(--theme_secondary_text_clr);
}
</style>

